import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type { ApiProblems } from 'types';

type ProblemCode =
  | 'CLIENT_ERROR'
  | 'SERVER_ERROR'
  | 'TIMEOUT_ERROR'
  | 'CONNECTION_ERROR'
  | 'NETWORK_ERROR'
  | 'UNKNOWN_ERROR'
  | 'CANCEL_ERROR';

type ApiOkResponse<T> = {
  ok: true;
  problem: null;
  originalError: null;
  data?: T;
  status?: number;
  headers?: Record<string, string>;
  config?: AxiosRequestConfig;
  duration?: number;
};

type ApiErrorResponse<T> = {
  ok: false;
  problem: ProblemCode;
  originalError: AxiosError<T>;
  data?: T;
  status?: number;
  headers?: Record<string, string>;
  config?: AxiosRequestConfig;
  duration?: number;
};

export type ApiResponse<T> = ApiOkResponse<T> | ApiErrorResponse<T>;

export type ApiClientConfig = AxiosRequestConfig & { baseURL?: string };

function toErrorResponse<T>(error: unknown, duration: number): ApiErrorResponse<T> {
  if (axios.isCancel(error)) {
    return {
      ok: false,
      problem: 'CANCEL_ERROR',
      originalError: error as AxiosError<T>,
      duration,
    };
  }

  if (!axios.isAxiosError(error)) {
    return {
      ok: false,
      problem: 'UNKNOWN_ERROR',
      originalError: error as AxiosError<T>,
      duration,
    };
  }

  const e = error as AxiosError<T>;

  if (e.message === 'Network Error') {
    return { ok: false, problem: 'NETWORK_ERROR', originalError: e, duration };
  }

  if (e.code === 'ECONNABORTED') {
    return { ok: false, problem: 'TIMEOUT_ERROR', originalError: e, duration };
  }

  if (e.code === 'ENOTFOUND' || e.code === 'ECONNREFUSED' || e.code === 'ECONNRESET') {
    return { ok: false, problem: 'CONNECTION_ERROR', originalError: e, duration };
  }

  const status = e.response?.status;
  if (status != null) {
    if (status >= 500) {
      return {
        ok: false,
        problem: 'SERVER_ERROR',
        originalError: e,
        status,
        data: e.response?.data as T,
        headers: e.response?.headers as Record<string, string>,
        config: e.config,
        duration,
      };
    }
    if (status >= 400) {
      return {
        ok: false,
        problem: 'CLIENT_ERROR',
        originalError: e,
        status,
        data: e.response?.data as T,
        headers: e.response?.headers as Record<string, string>,
        config: e.config,
        duration,
      };
    }
  }

  if (!e.response) {
    return { ok: false, problem: 'CONNECTION_ERROR', originalError: e, duration };
  }

  return {
    ok: false,
    problem: 'UNKNOWN_ERROR',
    originalError: e,
    status: status ?? undefined,
    duration,
  };
}

async function postWithApisauceShape<T>(
  client: AxiosInstance,
  url: string,
  data?: unknown,
  axiosConfig?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const start = Date.now();
  try {
    const response = await client.post<T>(url, data, axiosConfig);
    const duration = Date.now() - start;
    return {
      ok: true,
      problem: null,
      originalError: null,
      data: response.data,
      status: response.status,
      headers: response.headers as Record<string, string>,
      config: response.config,
      duration,
    };
  } catch (err) {
    const duration = Date.now() - start;
    return toErrorResponse<T>(err, duration);
  }
}

function getApiProblem(response: ApiResponse<any>): ApiProblems {
  let problem: ApiProblems = { kind: 'unknown' };
  switch (response.problem) {
    case 'CONNECTION_ERROR':
      problem = { kind: 'cannot-connect', temporary: true };
      break;
    case 'NETWORK_ERROR':
      problem = { kind: 'cannot-connect', temporary: true };
      break;
    case 'TIMEOUT_ERROR':
      problem = { kind: 'timeout', temporary: true };
      break;
    case 'SERVER_ERROR':
      problem = { kind: 'server', error: response.originalError };
      break;
    case 'CLIENT_ERROR':
      switch (response.status) {
        case 401:
          problem = { kind: 'unauthorized' };
          break;
        case 403:
          problem = { kind: 'forbidden' };
          break;
        case 404:
          problem = { kind: 'not-found' };
          break;
        default:
          problem = { kind: 'rejected' };
      }
      break;
    case 'CANCEL_ERROR':
      problem = { kind: 'cancelled' };
      break;
    case 'UNKNOWN_ERROR':
    default:
  }

  if (response.originalError) {
    problem.error = response.originalError;
  }

  return problem;
}

export default class Api {
  private readonly client: AxiosInstance;

  constructor(config: ApiClientConfig) {
    const { headers: inputHeaders, ...rest } = config;
    this.client = axios.create({
      timeout: 0,
      ...rest,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(inputHeaders as Record<string, string> | undefined),
      },
    });
  }

  post = async <T>(path: string, params?: any): Promise<{ kind: 'ok'; data: T } | ApiProblems> => {
    const response = await postWithApisauceShape<T>(this.client, path, params);

    if (response.ok) {
      return { kind: 'ok', data: response.data as T };
    }
    return getApiProblem(response);
  };

  download = async (
    path: string,
    params?: any
  ): Promise<{ kind: 'ok'; data: Blob | undefined } | ApiProblems> => {
    const response = await postWithApisauceShape<Blob>(this.client, path, params, {
      responseType: 'blob',
    });

    if (response.ok) {
      return { kind: 'ok', data: response.data };
    }
    return getApiProblem(response);
  };

  upload = async <T>(
    path: string,
    files: File[],
    params?: CustomObject<string>
  ): Promise<{ kind: 'ok'; data: T | undefined } | ApiProblems> => {
    const data = new FormData();
    Object.entries(params ?? {}).forEach(([field, value]) => {
      data.append(field, value);
    });
    files.forEach((file) => {
      data.append('files', file);
    });
    const response = await postWithApisauceShape<T>(this.client, path, data);

    if (response.ok) {
      return { kind: 'ok', data: response.data };
    }
    return getApiProblem(response);
  };

  graphql = async <T>(
    path: string,
    def: string,
    query: string,
    variables?: any
  ): Promise<{ kind: 'ok'; data: T } | ApiProblems> => {
    const response = await postWithApisauceShape<any>(this.client, path, {
      query,
      variables,
    });
    if (response.ok) {
      return { kind: 'ok', data: response.data?.data?.[def] };
    }
    return getApiProblem(response);
  };
}
